import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '@/lib/api';
import {
  FileText, Image as ImageIcon, FileType, File as FileIcon,
  CheckCircle, Clock, Cog, XCircle, Loader2,
  Eye, Download, Trash2, RotateCcw, X, ListChecks,
} from 'lucide-react';

const CATEGORY_TABS = [
  { value: 'all',                 label: 'All' },
  { value: 'statements',          label: 'Statements' },
  { value: 'tax_forms',           label: 'Tax Forms' },
  { value: 'loan_documents',      label: 'Loan Documents' },
  { value: 'legal_rbco',          label: 'Legal / RBCO' },
  { value: 'beneficiary',         label: 'Beneficiary' },
  { value: 'withdrawal_support',  label: 'Withdrawal Support' },
  { value: 'personnel_records',   label: 'Personnel Records' },
];

const STATUS_META = {
  verified:     { label: 'Verified',     icon: CheckCircle, cls: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
  under_review: { label: 'Under Review', icon: Clock,       cls: 'bg-blue-50 border-blue-200 text-blue-700' },
  processing:   { label: 'Processing',   icon: Cog,         cls: 'bg-amber-50 border-amber-200 text-amber-700' },
  rejected:     { label: 'Rejected',     icon: XCircle,     cls: 'bg-red-50 border-red-200 text-red-700' },
};

const niceSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return iso; }
};

const fileIcon = (ext, mime) => {
  const e = (ext || '').toLowerCase().replace('.', '');
  if (e === 'pdf' || mime === 'application/pdf') return { Icon: FileText, color: '#B91C1C', typeLabel: 'PDF' };
  if (['jpg','jpeg','png'].includes(e) || (mime || '').startsWith('image/')) return { Icon: ImageIcon, color: '#2D5F8A', typeLabel: 'IMAGE' };
  if (['doc','docx'].includes(e) || (mime || '').includes('word')) return { Icon: FileType, color: '#1E40AF', typeLabel: 'WORD' };
  return { Icon: FileIcon, color: '#6B7280', typeLabel: 'FILE' };
};


// ────────────────────────────────────────────────────────────────
// Preview modal — inline viewer for PDFs + images
// ────────────────────────────────────────────────────────────────
function PreviewModal({ doc, onClose }) {
  const [blobUrl, setBlobUrl] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!doc) return;
    let active = true;
    let url = '';
    (async () => {
      try {
        const res = await api.get(`/documents/${doc.id}/download`, { responseType: 'blob' });
        if (!active) return;
        url = URL.createObjectURL(new Blob([res.data], { type: doc.mimeType }));
        setBlobUrl(url);
      } catch (e) {
        if (!active) return;
        setErr(e?.response?.data?.detail || 'Unable to load preview.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [doc]);

  if (!doc) return null;
  const { typeLabel } = fileIcon(doc.extension, doc.mimeType);
  const canPreviewInline = typeLabel === 'PDF' || typeLabel === 'IMAGE';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="preview-modal">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E2E5E9]">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded bg-[#1B2A4A] text-white">{typeLabel}</span>
            <h3 className="text-sm font-semibold text-[#1B2A4A] truncate">{doc.filename}</h3>
          </div>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#374151]" data-testid="close-preview">
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-auto bg-gray-100 flex items-center justify-center p-4">
          {loading ? (
            <Loader2 size={20} className="animate-spin text-[#6B7280]" />
          ) : err ? (
            <p className="text-sm text-red-600" data-testid="preview-error">{err}</p>
          ) : canPreviewInline && typeLabel === 'PDF' ? (
            <iframe src={blobUrl} title={doc.filename} className="w-full h-[70vh] bg-white rounded" data-testid="preview-pdf" />
          ) : canPreviewInline && typeLabel === 'IMAGE' ? (
            <img src={blobUrl} alt={doc.filename} className="max-w-full max-h-[70vh] object-contain" data-testid="preview-image" />
          ) : (
            <div className="text-center" data-testid="preview-unsupported">
              <FileText size={32} className="mx-auto text-[#D1D5DB] mb-2" />
              <p className="text-sm text-[#6B7280] mb-2">Inline preview not available for this file type.</p>
              <a href={blobUrl} download={doc.filename}
                 className="inline-flex items-center gap-1 h-9 px-4 rounded-full text-xs font-medium bg-[#1B2A4A] text-white">
                <Download size={11} /> Download to view
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────
// Required documents checklist card
// ────────────────────────────────────────────────────────────────
function PendingActionsCard({ actions, onUploadClick }) {
  if (!actions || actions.length === 0) return null;
  return (
    <div className="panel p-0 overflow-hidden" data-testid="pending-actions-card">
      <div className="px-5 py-3 border-b border-[#E2E5E9] bg-amber-50">
        <div className="flex items-center gap-2">
          <ListChecks size={14} className="text-amber-700" />
          <h2 className="text-sm font-semibold text-amber-900 uppercase tracking-wider">
            Documents Required for Pending Actions
          </h2>
        </div>
      </div>
      <div className="divide-y divide-[#F0F1F3]">
        {actions.map(a => (
          <div key={a.type} className="px-5 py-4" data-testid={`pending-action-${a.type}`}>
            <p className="text-xs font-semibold text-[#1B2A4A] mb-2">
              Documents required for <span className="text-[#B45309]">{a.label}</span>:
            </p>
            <ul className="space-y-1.5">
              {a.requiredDocuments.map((rd, idx) => {
                const StatusIcon = rd.submitted ? CheckCircle : Clock;
                return (
                  <li key={idx}
                      className="flex items-center gap-2 text-xs"
                      data-testid={`required-doc-${a.type}-${idx}`}>
                    <StatusIcon size={12} className={rd.submitted ? 'text-emerald-600' : 'text-amber-600'} />
                    <span className={rd.submitted ? 'text-[#374151] line-through decoration-emerald-400' : 'text-[#374151]'}>
                      {rd.name}
                    </span>
                    {rd.submitted ? (
                      <span className="text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                        SUBMITTED
                      </span>
                    ) : (
                      <span className="text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                        MISSING
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────
// Main library
// ────────────────────────────────────────────────────────────────
export const DocumentLibrary = ({ reloadSignal, onReupload }) => {
  const [docs, setDocs] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [previewDoc, setPreviewDoc] = useState(null);

  const load = useCallback(async () => {
    try {
      const [dRes, aRes] = await Promise.allSettled([
        api.get('/documents'),
        api.get('/participant/pending-actions'),
      ]);
      if (dRes.status === 'fulfilled') setDocs(dRes.value.data || []);
      if (aRes.status === 'fulfilled') setActions(aRes.value.data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, reloadSignal]);

  const filtered = useMemo(
    () => activeFilter === 'all' ? docs : docs.filter(d => d.category === activeFilter),
    [docs, activeFilter],
  );

  const counts = useMemo(() => {
    const map = { all: docs.length };
    for (const d of docs) map[d.category] = (map[d.category] || 0) + 1;
    return map;
  }, [docs]);

  const handleDownload = async (doc) => {
    setBusyId(doc.id);
    try {
      const res = await api.get(`/documents/${doc.id}/download`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: doc.mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = doc.filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } finally { setBusyId(null); }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Remove "${doc.filename}"? This action is logged to the audit trail.`)) return;
    setBusyId(doc.id);
    try {
      await api.delete(`/documents/${doc.id}`);
      await load();
    } finally { setBusyId(null); }
  };

  return (
    <div className="space-y-3">
      <PendingActionsCard actions={actions} onUploadClick={onReupload} />

      <div className="panel p-0 overflow-hidden" data-testid="documents-library">
        {/* Filter tabs */}
        <div className="border-b border-[#E2E5E9] bg-[#FAFBFC] overflow-x-auto" data-testid="library-filter-tabs">
          <div className="flex items-center gap-1 px-3 py-2 min-w-max">
            {CATEGORY_TABS.map(t => {
              const n = counts[t.value] ?? 0;
              const isActive = activeFilter === t.value;
              return (
                <button key={t.value}
                        onClick={() => setActiveFilter(t.value)}
                        data-testid={`filter-tab-${t.value}`}
                        className={
                          'inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[11px] font-medium transition-colors ' +
                          (isActive
                            ? 'bg-[#1B2A4A] text-white'
                            : 'bg-white text-[#6B7280] border border-[#D1D5DB] hover:border-[#1B2A4A]')
                        }>
                  {t.label}
                  <span className={
                    'text-[9px] font-mono px-1.5 py-0.5 rounded ' +
                    (isActive ? 'bg-white/20 text-white' : 'bg-gray-100 text-[#6B7280]')
                  }>
                    {n}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {loading ? (
          <div className="p-6 text-center text-xs text-[#9CA3AF]">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center" data-testid="library-empty">
            <FileText size={28} className="mx-auto text-[#D1D5DB] mb-2" />
            <p className="text-sm text-[#6B7280]">
              {activeFilter === 'all' ? 'No documents uploaded yet.' : 'No documents in this category.'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-[#F0F1F3]">
            {filtered.map(d => (
              <LibraryRow
                key={d.id} d={d} busy={busyId === d.id}
                onPreview={() => setPreviewDoc(d)}
                onDownload={() => handleDownload(d)}
                onDelete={() => handleDelete(d)}
                onReupload={() => onReupload?.(d)}
              />
            ))}
          </div>
        )}
      </div>

      <PreviewModal doc={previewDoc} onClose={() => setPreviewDoc(null)} />
    </div>
  );
};


function LibraryRow({ d, busy, onPreview, onDownload, onDelete, onReupload }) {
  const { Icon: TypeIcon, color, typeLabel } = fileIcon(d.extension, d.mimeType);
  const statusMeta = STATUS_META[d.status] || STATUS_META.under_review;
  const StatusIcon = statusMeta.icon;

  return (
    <div className="px-5 py-3 flex items-center gap-3" data-testid={`doc-row-${d.id}`}>
      <div className="w-9 h-9 rounded flex items-center justify-center border border-[#E2E5E9]"
           style={{ backgroundColor: `${color}10` }}>
        <TypeIcon size={15} style={{ color }} data-testid={`doc-type-icon-${d.id}`} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-medium text-[#1B2A4A] truncate" data-testid={`doc-name-${d.id}`}>
            {d.filename}
          </p>
          <span className="text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-gray-100 border border-[#D1D5DB] text-[#6B7280]"
                data-testid={`doc-filetype-${d.id}`}>
            {typeLabel}
          </span>
          <span className="text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-[#1B2A4A] text-white"
                data-testid={`doc-category-${d.id}`}>
            {d.categoryLabel.toUpperCase()}
          </span>
          <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded border ${statusMeta.cls}`}
                data-testid={`doc-status-${d.id}`}>
            <StatusIcon size={10} />
            {statusMeta.label}
          </span>
        </div>
        <p className="text-[10px] font-mono text-[#9CA3AF] mt-0.5">
          {niceSize(d.sizeBytes)} · Uploaded {fmtDate(d.uploadedAt)} · Retained until {fmtDate(d.retentionUntilAt)}
        </p>
        {d.status === 'rejected' && d.rejectionReason && (
          <p className="text-[11px] text-red-700 mt-1" data-testid={`doc-rejection-${d.id}`}>
            <b>Reason:</b> {d.rejectionReason}
          </p>
        )}
      </div>

      <div className="flex items-center gap-1 shrink-0">
        <button onClick={onPreview} disabled={busy}
                title="Preview"
                data-testid={`preview-doc-${d.id}`}
                className="h-7 w-7 rounded text-[#6B7280] hover:text-[#1B2A4A] hover:bg-gray-50 border border-transparent hover:border-[#D1D5DB] inline-flex items-center justify-center disabled:opacity-50">
          <Eye size={12} />
        </button>
        <button onClick={onDownload} disabled={busy}
                title="Download"
                data-testid={`download-doc-${d.id}`}
                className="h-7 w-7 rounded text-[#6B7280] hover:text-[#1B2A4A] hover:bg-gray-50 border border-transparent hover:border-[#D1D5DB] inline-flex items-center justify-center disabled:opacity-50">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
        </button>
        {d.status === 'rejected' && (
          <button onClick={onReupload} disabled={busy}
                  title="Re-upload replacement"
                  data-testid={`reupload-doc-${d.id}`}
                  className="h-7 px-2 rounded text-[10px] font-medium text-amber-700 border border-amber-300 hover:bg-amber-50 inline-flex items-center gap-1 disabled:opacity-50">
            <RotateCcw size={10} /> Re-upload
          </button>
        )}
        <button onClick={onDelete} disabled={busy}
                title="Remove"
                data-testid={`delete-doc-${d.id}`}
                className="h-7 w-7 rounded text-[#9CA3AF] hover:text-red-600 hover:bg-red-50 border border-transparent hover:border-red-200 inline-flex items-center justify-center disabled:opacity-50">
          <Trash2 size={12} />
        </button>
      </div>
    </div>
  );
}

export default DocumentLibrary;
